import { IonIcon, IonSpinner } from '@ionic/react'
import dayjs from 'dayjs'
import { logoEuro } from 'ionicons/icons'
import React, { FC, useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
	ConsumptionData,
	ConsumptionDataContext,
} from '../../../../contexts/ConsumptionDataContext'
import useUser from '../../../../contexts/hooks/useUser'
import { ObjectContext } from '../../../../contexts/objectContext/ObjectContext'
import { useContract } from '../../../../hooks/useContract'
import { getMonthString } from '../../../../modules/consumptionDataParser'
import { useProductDiscounts } from '../../../contractInformation/hooks/useProductDiscounts'

interface MoneyCircleProps {
	consideredMonth: ConsumptionData
}

const MoneyCircle: FC<MoneyCircleProps> = ({ consideredMonth }) => {
	const { currentMonthInvoiceAccumulation, invoiceLoading } = useContext(ConsumptionDataContext)
	const { price, showMonthlyPrice } = useContext(ObjectContext)

	const { user } = useUser()
	const { t } = useTranslation('pages_overview')

	const { contractData: contract } = useContract()

	const productId = contract?.Products?.[0]?.SupplyProductID ?? null
	const { total } = useProductDiscounts(productId, contract?.StartDate ?? null)

	const isInFreeMonthsPeriod = useMemo(() => {
		if (!contract?.StartDate || !total.freeMonthlyFeeMonths) return false

		const contractStartDate = dayjs(contract.StartDate)
		const freeMonthsEndDate = contractStartDate.add(total.freeMonthlyFeeMonths, 'months')
		const currentDate = dayjs()

		return currentDate.isBetween(contractStartDate, freeMonthsEndDate)
	}, [contract?.StartDate, total.freeMonthlyFeeMonths])

	const invoiceAccumulationWithMonthlyPrice = useMemo(() => {
		if (!currentMonthInvoiceAccumulation) return 0

		if (isInFreeMonthsPeriod) {
			return Number(currentMonthInvoiceAccumulation.toFixed(2))
		}

		const daysInMonth = dayjs().daysInMonth()
		const currentDay = dayjs().date()
		const factor = currentDay / daysInMonth
		const slicedMonthlyPrice = (price.monthlyPrice ?? 0) * factor

		return Number((currentMonthInvoiceAccumulation + slicedMonthlyPrice).toFixed(2))
	}, [currentMonthInvoiceAccumulation, isInFreeMonthsPeriod, price.monthlyPrice])

	const value = Math.max(
		showMonthlyPrice ? invoiceAccumulationWithMonthlyPrice : (currentMonthInvoiceAccumulation ?? 0),
		0
	)

	return (
		<div className='slide-container '>
			<div id='circle' className='bg-white dark:bg-background-200-dark'>
				<div
					id='inner'
					className='bg-white dark:bg-background-200-dark text-text-dark dark:text-text-light'
				>
					<IonIcon
						icon={logoEuro}
						className='text-brand-primary-100-light mb-1 dark:text-brand-primary-100-dark'
					/>
					<span className='current-month-title text-text-dark dark:text-text-light'>
						{getMonthString(consideredMonth.localTime.month, user?.language)}
					</span>
					<span className='main-value'>
						{invoiceLoading ? (
							<IonSpinner name='crescent' />
						) : (
							<>
								<span className='text-text-dark dark:text-text-light'>
									{Math.floor(value).toFixed(0)}
								</span>
								<span className='cents text-text-dark dark:text-text-light'>
									,{value.toFixed(2).split('.')[1]}
								</span>

								<span className='unit text-text-grey-300 dark:text-text-grey-100'>€</span>
							</>
						)}
					</span>
					<div className='bottom text-text-grey'>{t('circles.arvio')}</div>
				</div>
			</div>
		</div>
	)
}

export default MoneyCircle
